import {
  AdditionalInfo,
  CreativeIdFormat,
  JobExecution,
  JobRelativeDuration,
  JobStatus,
  JobType,
  RemoteJobExecution,
  RemoteScheduledJob,
  RemoteScheduledJobData,
  ScheduledJob,
  ScheduledJobData,
} from './jobs.types';
import forceTimeZone from '../timezones/utils/forceTimeZone';
import {DateTime} from 'luxon';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import _reduce from 'lodash/reduce';
import stringToDateTime from '../timezones/utils/stringToDateTime';
import _omit from 'lodash/omit';

/*****
 * Backend are sending iso string defected (0001-01-01T00:00:00) which provides
 * */
const minValidYear = 2000;

function toUTCDateTime(dateString: string) {
  // Noy Shargal: where ever you don't get TZ from server - consider it as UTC
  const [date] = dateString.split('Z');

  return forceTimeZone(DateTime.fromISO(date), 0);
}

function getDateRange(jobData: RemoteScheduledJobData) {
  switch (jobData?.relativeDuration) {
    case JobRelativeDuration.FromDay:
      return `From ${jobData.fromDate} till yesterday`;
    case JobRelativeDuration.LastDays:
      return `Last ${jobData.customLastDays} days`;
    default:
      return `${jobData.fromDate} - ${jobData.toDate}`;
  }
}

function getCreativesString(remoteCreativeValues: CreativeIdFormat[]): string {
  return _reduce(
    remoteCreativeValues,
    (resultString, item) => {
      return resultString + `creative: ${item.creativeId}, format: ${item.adSize}. `;
    },
    '',
  );
}

function toJobData(remoteJobData: string) {
  const jobData = JSON.parse(remoteJobData) as RemoteScheduledJobData;

  return {
    ...jobData,
    ...(jobData.fromDate && {
      fromDate: toUTCDateTime(jobData.fromDate),
    }),
    ...(jobData.toDate && {
      toDate: toUTCDateTime(jobData.toDate),
    }),
    ...(jobData?.emailRecipients?.$values && {
      recipients: jobData?.emailRecipients?.$values.join(),
    }),
    ...(jobData.fromDate && jobData.toDate && {dateRange: getDateRange(jobData)}),
    ...(jobData.creativeIdsAndFormats?.$values && {
      creativeIdsAndFormats: getCreativesString(jobData.creativeIdsAndFormats?.$values),
    }),
    ...(jobData.campaignIds?.$values && {
      campaignIds: jobData.campaignIds?.$values.join(','),
    }),
  } as ScheduledJobData;
}

function toLocalScheduledJob(remoteJob: RemoteScheduledJob) {
  return {
    ...remoteJob,
    schedule: remoteJob.timing ? remoteJob.timing.cronDescriptor : '--',
    nextRun: remoteJob.timing?.cronNextRun
      ? DateTime.fromISO(remoteJob.timing.cronNextRun, {setZone: true})
      : null,
    jobData: remoteJob.jobStr ? toJobData(remoteJob.jobStr) : null,
    jobType: remoteJob.jobType as JobType,
    ...(remoteJob.lastEnqueueTime && {
      lastEnqueueTime: stringToDateTime(remoteJob.lastEnqueueTime),
    }),
    ...(remoteJob.lastExecTime && {
      lastExecTime: stringToDateTime(remoteJob.lastExecTime),
    }),
    lastExecStatus: remoteJob.lastExecStatus as JobStatus,
  } as ScheduledJob;
}

function toLocalJobExecution<JobData = ScheduledJobData>(
  remote: RemoteJobExecution,
  campaignEnums: Record<string, CLEnumsMapper>,
): JobExecution<JobData> {
  function getAdditionalInfo(remote: RemoteJobExecution): AdditionalInfo | null {
    try {
      return JSON.parse(remote.additionalInfo) as AdditionalInfo;
    } catch (e) {
      return null;
    }
  }

  function getJobData(remote: RemoteJobExecution) {
    try {
      const parsed = JSON.parse(remote.jobStr);

      return {
        ...parsed,
      };
    } catch (e) {
      return remote.jobStr;
    }
  }

  return {
    ..._omit(remote, ['execStatus', 'additionalInfo']),
    startTime: remote.startTime ? DateTime.fromISO(remote.startTime).toUTC() : null,
    endTime: remote.endTime ? DateTime.fromISO(remote.endTime).toUTC() : null,
    enqueueTime: remote.enqueueTime ? DateTime.fromISO(remote.enqueueTime).toUTC() : null,
    initiatedAtTime: remote.initiatedAtTime
      ? DateTime.fromISO(remote.initiatedAtTime).toUTC()
      : null,
    ...(remote.execStatus && {
      execStatus: campaignEnums[CLEnumKeys.ExcelReport.JobExecution].byValue[remote.execStatus],
      executionType: remote.isManual ? 'Manual' : 'Automatic',
      additionalInfo: getAdditionalInfo(remote),
      jobData: getJobData(remote),
    }),
  };
}

export {toLocalScheduledJob, toLocalJobExecution, minValidYear, toUTCDateTime, toJobData};
